/*
 * Copyright (c) 2023. Bolmate B.V.
 */
import './style/salesDashboard.scss';

import {
    AwesomeIcon,
    ChangeFunction,
    DvrdSelect,
    getScreenHeight,
    IndexedObject,
    Loader,
    SelectItemShape
} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {Column, ColumnFilterApplyClickEvent} from 'primereact/column';

import {ColumnGroup} from 'primereact/columngroup';

import {DataTable, DataTableFilterMeta, DataTablePageEvent, DataTableStateEvent} from 'primereact/datatable';

import {InputText} from 'primereact/inputtext';

import {MultiSelect, MultiSelectChangeEvent} from 'primereact/multiselect';

import {Row} from 'primereact/row';

import React, {ChangeEventHandler, MouseEventHandler, ReactElement, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {AccessKey, PrimeSorting, SalesDashboardPeriod, SalesDashboardProduct} from '../../util/interfaces';

import {
    ColumnConfig,
    ColumnGroupConfig,
    getSelectableColumnGroups,
    getSalesDashboardColumns
} from '../../util/salesDashboardUtil';

import {BolmateSelect} from '../controls';

import {numberTrim, parseMoney, parseThousandSeparator} from '../../util/utils';

import classNames from 'classnames';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip
} from 'chart.js';

import {Line} from 'react-chartjs-2';

import InfoIcon from '../icon/infoIcon';

import {AppContext} from '../misc/appContext';

import useRight from '../misc/useRight';

import Account from '../../models/accountModel';

import InfoBanner from '../misc/infoBanner';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface ChartProps {
    loadingChart: boolean;
    chartData: IndexedObject<number>;
    period: SalesDashboardPeriod;
}

interface TableProps {
    onSort: (evt: DataTableStateEvent) => void;
    onChangeSearch: ChangeEventHandler<HTMLInputElement>;
    onChangePage: (evt: DataTablePageEvent) => void;
    data: Array<SalesDashboardProduct>;
    onToggleColumn: (evt: MultiSelectChangeEvent) => void;
    onChangeFilter: (evt: ColumnFilterApplyClickEvent) => void;
    onClearFilter: (field: string) => VoidFunction;
    onReload: MouseEventHandler;
    onClickImportProductCosts: MouseEventHandler;
    onExport: MouseEventHandler;
    onCombineAccount: ChangeFunction<Array<string>>;
    totalRows: number;
    firstRowNum: number;
    sortField: string;
    orderDirection: PrimeSorting;
    search: string;
    limit: number;
    visibleColumns: Array<ColumnConfig>;
    filters: DataTableFilterMeta;
    loadingProducts: boolean;
    combineAccounts: Set<string>;
}

type Props = ChartProps &
    TableProps & {
        onChangePeriod: ChangeFunction<SalesDashboardPeriod>;
        onConfigureCosts: (product: SalesDashboardProduct) => MouseEventHandler;
        loadingStats: boolean;
        period: SalesDashboardPeriod;
        totalRevenue: number;
        totalQuantity: number;
        totalProfit: number;
        totalMargin: number;
        prevRevenue: number;
        prevQuantity: number;
        prevProfit: number;
        prevMargin: number;
    };

export default function SalesDashboard(props: Props) {
    const {t} = useTranslation();
    const periods: Array<SelectItemShape> = useMemo(
        () => [
            {label: t('salesDashboard.period.today', 'Vandaag'), value: SalesDashboardPeriod.TODAY},
            {label: t('salesDashboard.period.yesterday', 'Gisteren'), value: SalesDashboardPeriod.YESTERDAY},
            {label: t('salesDashboard.period.sevenDays', 'Afgelopen 7 dagen'), value: SalesDashboardPeriod.SEVEN_DAYS},
            {
                label: t('salesDashboard.period.thirtyDays', 'Afgelopen 30 dagen'),
                value: SalesDashboardPeriod.THIRTY_DAYS
            },
            {label: t('salesDashboard.period.total', 'Afgelopen 90 dagen'), value: SalesDashboardPeriod.TOTAL},
            {label: t('salesDashboard.period.currentMonth', 'Deze maand'), value: SalesDashboardPeriod.CURRENT_MONTH},
            {label: t('salesDashboard.period.lastMonth', 'Vorige maand'), value: SalesDashboardPeriod.LAST_MONTH},
            {label: t('salesDashboard.period.custom', 'Aangepast...'), value: SalesDashboardPeriod.CUSTOM}
        ],
        [t]
    );
    const {accounts, account} = useContext(AppContext).accountContext;
    const {
        onChangePeriod,
        loadingStats,
        period,
        totalRevenue,
        totalQuantity,
        totalProfit,
        totalMargin,
        prevRevenue,
        prevQuantity,
        prevProfit,
        prevMargin,
        combineAccounts,
        onCombineAccount
    } = props;
    const accountItems: Array<SelectItemShape> = useMemo(() => {
        return accounts
            .filter((acc: Account) => acc.id !== account?.id)
            .map((acc: Account) => ({
                value: acc.id,
                label: acc.labelWithSellerID
            }));
    }, [account, accounts]);

    function renderInfo() {
        const message = t(
            'salesDashboard.infoMessage',
            `We berekenen deze cijfers aan de hand van de binnenkomende orders en retouren van Bol.
                Deze cijfers zijn niet altijd hetzelfde als degene die bol zelf in zijn portaal gebruikt.
                Gebruik deze cijfers daarom alleen ter indicatie.
                Voor hulp bij de BTW-aangifte, hebben we de BTW-aangifte tool.`
        );
        return (
            <InfoBanner variant='info' icon={<InfoIcon />} persistDismiss={false}>
                {message}
            </InfoBanner>
        );
    }

    function renderStats() {
        const revPercentage = Math.abs(Math.round(((totalRevenue - prevRevenue) / (prevRevenue || 1)) * 100));
        const profitPercentage = Math.abs(Math.round(((totalProfit - prevProfit) / (prevProfit || 1)) * 100));
        const quantityPercentage = Math.abs(Math.round(((totalQuantity - prevQuantity) / (prevQuantity || 1)) * 100));
        const marginPercentage = Math.abs(Math.round(((totalMargin - prevMargin) / (prevMargin || 1)) * 100));
        return (
            <div className='stats-container'>
                {renderStat(
                    t('salesDashboard.stats.revenue', 'OMZET'),
                    `\u20ac${parseMoney(totalRevenue, true)}`,
                    revPercentage,
                    `\u20ac${parseMoney(prevRevenue, true)}`,
                    totalRevenue >= prevRevenue,
                    {title: `\u20ac${parseMoney(totalRevenue)}`}
                )}
                {renderStat(
                    t('salesDashboard.stats.quantity', 'AANTAL VERKOCHT'),
                    `${parseThousandSeparator(totalQuantity)}`,
                    quantityPercentage,
                    `${parseThousandSeparator(prevQuantity)}`,
                    totalQuantity >= prevQuantity,
                    {title: totalQuantity}
                )}
                {renderStat(
                    t('salesDashboard.stats.profit', 'WINST'),
                    `\u20ac${parseMoney(totalProfit, true)}`,
                    profitPercentage,
                    `\u20ac${parseMoney(prevProfit, true)}`,
                    totalProfit >= prevProfit,
                    {
                        infoTitle: t('salesDashboard.stats.profitInfoTitle', 'De winst'),
                        title: `\u20ac${parseMoney(totalProfit)}`
                    }
                )}
                {renderStat(
                    t('salesDashboard.stats.margin', 'WINSTMARGE'),
                    `${totalMargin.toString().replace('.', ',')}%`,
                    marginPercentage,
                    `${prevMargin.toString().replace('.', ',')}%`,
                    totalMargin >= prevMargin,
                    {
                        infoTitle: t('salesDashboard.stats.marginInfoTitle', 'De winstmarge'),
                        title: totalMargin
                    }
                )}
            </div>
        );
    }

    function renderStat(
        label: string,
        diff: number | string,
        percentage: number,
        total: number | string | null,
        positive: boolean,
        config?: {infoTitle?: string; title?: string | number}
    ) {
        const {infoTitle, title} = config ?? {};
        const caret: IconName = positive ? 'caret-up' : 'caret-down';
        return (
            <div className='stat-container'>
                <Loader active={loadingStats} />
                {!!infoTitle && (
                    <div className='stat-info-container'>
                        <InfoIcon scale={0.75} />
                        <p className='stat-info-message'>
                            {infoTitle}{' '}
                            {t(
                                'salesDashboard.stats.calculatedWithCosts',
                                'wordt enkel berekend met producten waarvoor de kosten zijn ingesteld.'
                            )}
                        </p>
                    </div>
                )}
                <span className='sub-text'>{label}</span>
                <div className='bottom-container'>
                    <span className='main-text' title={title?.toString()}>
                        {diff}
                    </span>
                    {!!total && period !== SalesDashboardPeriod.TOTAL && (
                        <>
                            <div className={classNames('percentage-container', positive ? 'positive' : 'negative')}>
                                <AwesomeIcon name={caret} prefix='fas' />
                                <span>{percentage}%</span>
                            </div>
                        </>
                    )}
                </div>
                {period !== SalesDashboardPeriod.TOTAL && (
                    <span className='sub-text'>
                        {t('salesDashboard.stats.previousPeriod', 'VORIGE PERIODE')} {total}
                    </span>
                )}
            </div>
        );
    }

    return (
        <>
            <InfoBanner
                featureKey='sales_dashboard'
                variant='info'
                title={t('salesDashboard.bannerTitle', 'Verkoopdashboard')}
                showForNewUsersOnly={true}
            >
                {t(
                    'salesDashboard.bannerBody',
                    'Analyseer je verkopen, winst en omzet per product en periode in één overzicht.'
                )}
            </InfoBanner>
            <div className='sales-dashboard'>
                {renderInfo()}
                <div className='top-container'>
                    <div className='chart-container'>
                        <span className='sub-text'>
                            {t('salesDashboard.totalPeriodRevenue', 'TOTALE PERIODE OMZET')}
                        </span>
                        <div>
                            <span className='main-text'>&euro;{numberTrim(totalRevenue, true)}</span>
                        </div>
                        <SalesChart {...props} />
                    </div>
                    <div className='stat-period-container'>
                        <div className='selector-container'>
                            {accounts.length > 1 ? (
                                <DvrdSelect<any>
                                    items={accountItems}
                                    value={Array.from(combineAccounts)}
                                    label={t('salesDashboard.combineSales', 'Sales combineren')}
                                    onChange={onCombineAccount}
                                    placeholder={t('salesDashboard.chooseAccounts', 'Kies account(s)')}
                                    className='combine-accounts-select'
                                    multi
                                    submitOnClose
                                />
                            ) : (
                                <div />
                            )}
                            <BolmateSelect
                                value={period}
                                onChange={onChangePeriod}
                                items={periods}
                                itemsHeight='30rem'
                                itemContainerClassName='period-items'
                                highlightSelected
                            />
                        </div>
                        {renderStats()}
                    </div>
                </div>
                <SalesTable {...props} />
            </div>
        </>
    );
}

function SalesTable(props: TableProps) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const [canCostPrice, canExport] = useRight(AccessKey.SALES_DASHBOARD_COST_PRICES, AccessKey.SALES_DASHBOARD_EXPORT);
    const {
        firstRowNum,
        totalRows,
        loadingProducts,
        sortField,
        onSort,
        limit,
        filters,
        onChangeFilter,
        onClearFilter,
        onChangeSearch,
        search,
        onChangePage,
        orderDirection,
        visibleColumns,
        onToggleColumn,
        onReload,
        onClickImportProductCosts,
        onExport
    } = props;
    const groups = useMemo(() => {
        const groups: Array<ColumnGroupConfig> = [];
        for (const grp of getSalesDashboardColumns().slice()) {
            const group = Object.assign({}, grp);
            if (group.label === '') {
                groups.push(group);
                continue;
            }
            group.columns = group.columns.filter((column: ColumnConfig) => columnIsVisible(column));
            if (group.columns.length) groups.push(group);
        }
        return groups;
    }, [visibleColumns]);
    const data = useMemo(() => {
        return props.data.map((data: SalesDashboardProduct) => ({...data}));
    }, [props.data]);

    function columnIsVisible(column: ColumnConfig) {
        if (column.label === '' || column.label === 'Naam') return true;
        return !!visibleColumns.find((col: ColumnConfig) => col.label === column.label);
    }

    function getRenderColumns(): Array<ColumnConfig> {
        return groups.reduce(
            (prev: Array<ColumnConfig>, current: ColumnGroupConfig) => prev.concat(current.columns),
            []
        );
    }

    function renderHeaderGroup() {
        const columnGroups = groups;
        const headerColumns: Array<ColumnConfig> = getRenderColumns();
        return (
            <ColumnGroup>
                <Row>
                    {columnGroups.map((columnGroup: ColumnGroupConfig, index: number) => (
                        <Column
                            key={index}
                            header={columnGroup.label}
                            colSpan={columnGroup.columns.length}
                            frozen={columnGroup.frozen}
                        />
                    ))}
                </Row>
                <Row>
                    {headerColumns.map((column: ColumnConfig, index: number) => (
                        <Column
                            key={index}
                            field={column.field}
                            header={column.label}
                            body={column.body}
                            style={!column.styleRowOnly ? column.style : undefined}
                            sortable={column.sortable}
                            className='table-head-column'
                            frozen={column.frozen}
                            filter={column.filter}
                            onFilterApplyClick={onChangeFilter}
                            onFilterClear={onClearFilter(column.field)}
                            dataType={column.dataType}
                        />
                    ))}
                </Row>
            </ColumnGroup>
        );
    }

    function renderHeader() {
        const headerColumns = getSelectableColumnGroups(getSalesDashboardColumns()).slice();
        return (
            <div className='table-header'>
                <div className='head-col'>
                    <span className='p-input-icon-left'>
                        <i className='pi pi-search' />
                        <InputText
                            value={search}
                            onChange={onChangeSearch}
                            placeholder={t('salesDashboard.search', 'Zoeken')}
                        />
                    </span>
                    <MultiSelect
                        value={visibleColumns}
                        options={headerColumns}
                        optionLabel='label'
                        optionGroupLabel='label'
                        optionGroupChildren='columns'
                        display='chip'
                        onChange={onToggleColumn}
                        style={{width: '25rem'}}
                    />
                </div>
                <div className='head-col'>
                    {canCostPrice && (
                        <div className='table-btn with-label' onClick={onClickImportProductCosts}>
                            <AwesomeIcon name='file-arrow-up' className='btn-icon' />
                            <label className='btn-label'>
                                {t('salesDashboard.uploadProductCosts', 'Productkosten uploaden')}
                            </label>
                        </div>
                    )}
                    <div className='table-btn' onClick={onReload}>
                        <AwesomeIcon name='arrows-rotate' className='btn-icon' />
                        <span className='tooltip'>{t('salesDashboard.recalculate', 'Opnieuw berekenen')}</span>
                    </div>
                    {canExport && (
                        <div className='table-btn' onClick={onExport}>
                            <AwesomeIcon name='file-excel' className='btn-icon' />
                            <span className='tooltip'>
                                {t('salesDashboard.exportToExcel', 'Exporteren naar Excel')}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderTableColumns() {
        const allColumns: Array<ColumnConfig> = getRenderColumns();
        return allColumns.map((column: ColumnConfig, index: number) => {
            let body: ((product: SalesDashboardProduct) => ReactElement | string | number) | undefined = undefined;
            if (column.functionBody) body = column.functionBody(props, context);
            else if (column.body) body = column.body;
            return (
                <Column
                    key={index}
                    field={column.field}
                    body={body}
                    style={column.style}
                    frozen={column.frozen}
                    filter={column.filter}
                    filterMatchModeOptions={column.matchModes}
                />
            );
        });
    }

    const screenHeight = getScreenHeight();
    const headerHeight = document.querySelector('div.bolmate-header')?.getBoundingClientRect().height ?? 0;
    const tableHeight = screenHeight - headerHeight;
    return (
        <div className='sales-table-container'>
            <DataTable
                value={data.slice()}
                lazy
                onPage={onChangePage}
                headerColumnGroup={renderHeaderGroup()}
                totalRecords={totalRows}
                header={renderHeader()}
                paginator
                rows={limit}
                showGridlines
                rowsPerPageOptions={[1, 5, 10, 25, 50]}
                first={firstRowNum}
                loading={loadingProducts}
                selectionMode='single'
                onSort={onSort}
                className='sales-table'
                sortField={sortField}
                sortOrder={orderDirection}
                filters={filters}
                emptyMessage={t('salesDashboard.emptyMessage', 'Geen verkopen gevonden')}
                defaultSortOrder={-1}
                scrollHeight={tableHeight + 'px'}
                scrollable
            >
                {renderTableColumns()}
            </DataTable>
        </div>
    );
}

function SalesChart(props: ChartProps) {
    const {t} = useTranslation();
    const {period, loadingChart} = props;
    const [chartData, chartOptions] = useMemo(() => {
        const labels: Array<string> = [];
        const data: Array<number> = [];
        const dates = Object.keys(props.chartData);
        for (const [date, revenue] of Object.entries(props.chartData)) {
            const momentDate = new IDate(date);
            const format = period === SalesDashboardPeriod.TODAY ? 'HH:mm' : 'D MMM';
            const chartLabel = momentDate.format(format);
            if (period === SalesDashboardPeriod.TOTAL) {
                if (momentDate.day() === 1 || dates.indexOf(date) === dates.length - 1) {
                    labels.push(chartLabel);
                    data.push(revenue);
                }
            } else {
                labels.push(chartLabel);
                data.push(revenue);
            }
        }
        const chartData = {
            labels,
            datasets: [
                {
                    label: t('salesDashboard.chart.revenueLabel', 'Omzet'),
                    data,
                    fill: true,
                    borderColor: 'rgb(37, 104, 239)',
                    backgroundColor: 'rgba(37, 104, 239, .2)',
                    tension: 0.3
                }
            ]
        };
        const documentStyle = getComputedStyle(document.documentElement);
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const limitTicks = [
            SalesDashboardPeriod.THIRTY_DAYS,
            SalesDashboardPeriod.TODAY,
            SalesDashboardPeriod.CUSTOM
        ].includes(period);
        // let minValue = Math.min(...Object.values(props.chartData).filter((value: number) => value > 0));
        // if (minValue === Infinity) minValue = 0;
        const chartOptions = {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        label: ({raw}: {raw: any}) => {
                            return `\u20ac${parseMoney(raw)}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    },
                    ticks: {
                        autoSkip: limitTicks,
                        maxTicksLimit: limitTicks ? 12 : undefined,
                        color: textColorSecondary
                    }
                },
                y: {
                    min: 0,
                    ticks: {
                        color: textColorSecondary,
                        callback: function (value: number) {
                            return Number.isInteger(value) ? value : null;
                        }
                    },
                    grid: {
                        display: false
                    }
                }
            }
        };
        return [chartData, chartOptions];
    }, [props.chartData, period, t]);

    return (
        <div className='sales-chart'>
            <Loader active={loadingChart} />
            <Line data={chartData} options={chartOptions} />
        </div>
    );
}
